import React, { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { peopleAPI } from "@/api/people";

interface PersonFieldProps {
  personId: string;
  onPersonChange: (personId: string) => void;
  label?: string;
  isGivenCheck?: boolean;
}

const PersonField: React.FC<PersonFieldProps> = ({
  personId,
  onPersonChange,
  label,
  isGivenCheck = true
}) => {
  // Default label based on check type
  const defaultLabel = isGivenCheck ? "Verilen Kişi" : "Alınan Kişi";
  // Use the provided label or the default
  const fieldLabel = label || defaultLabel;

  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch people from the API
  useEffect(() => {
    const fetchPeople = async () => {
      try {
        setIsLoading(true);
        const data = await peopleAPI.getPeople();
        
        // Map the data to simplified format for the dropdown
        const mappedPeople = data.map(person => ({
          id: person.id,
          name: person.name
        }));
        setPeople(mappedPeople);
      } catch (error) {
        
      } finally {
        setIsLoading(false);
      }
    };

    fetchPeople();
    
    // Add listener for people data refresh events
    const handlePeopleDataRefresh = (event: any) => {
      
      fetchPeople();
    };
    
    window.addEventListener('people-data-refresh', handlePeopleDataRefresh);
    
    return () => {
      window.removeEventListener('people-data-refresh', handlePeopleDataRefresh);
    };
  }, []);

  return (
    <div className="space-y-1.5">
      <label htmlFor="person" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
        {fieldLabel}
      </label>
      <Select
        value={personId}
        onValueChange={onPersonChange}
      >
        <SelectTrigger className="w-full border-gray-200 bg-white rounded-md h-10 text-[13px] text-gray-900 focus:border-brand-600 focus:ring-1 focus:ring-brand-300">
          {isLoading ? (
            <div className="flex items-center text-gray-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              <span className="text-[13px]">Yükleniyor...</span>
            </div>
          ) : (
            <SelectValue placeholder="Kişi seçin" />
          )}
        </SelectTrigger>
        <SelectContent>
          {people.map(person => (
            <SelectItem key={person.id} value={person.id}>
              {person.name}
            </SelectItem>
          ))}
          {people.length === 0 && !isLoading && (
            <div className="py-2 px-4 text-[13px] text-gray-500">
              Kişi bulunamadı
            </div>
          )}
          <SelectItem value="new" className="text-brand-600 font-semibold">
            <span className="flex items-center">
              <Plus className="mr-1 h-3 w-3" />
              + Yeni kişi
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export default PersonField;
